import React, { useState, useEffect, useCallback } from "react"
import { Row, Col, Spinner } from "reactstrap"
import { Link, useParams } from "react-router-dom"
import { AvForm, AvRadioGroup, AvRadio } from "availity-reactstrap-validation"
import api from "API/Api"
import SweetAlert from "react-bootstrap-sweetalert"
import { CC_SECTIONS, CC_EXPORT_PARAMS, CC_PRINT_PARAMS } from "./Components/Edit-Forms/ccSettingsConfig"

const CcMobileSettings = () => {
  const { ccId } = useParams()
  const [settings, setSettings] = useState(null)
  const [pageLoad, setPageLoad] = useState(true)
  const [saving, setSaving] = useState({})
  const [lastSaved, setLastSaved] = useState("")
  const [resetConfirm, setResetConfirm] = useState(false)

  const fetchSettings = useCallback(async () => {
    try {
      const resp = await api.get("get-settings/" + ccId + "?center_type=cc")
      setSettings(resp.data)
    } catch (e) { console.error("Failed to load settings:", e) }
    setPageLoad(false)
  }, [ccId])

  useEffect(() => { fetchSettings() }, [fetchSettings])

  // Auto-save radio/select change
  const changeHandler = async (name, val) => {
    // Optimistic update
    setSettings(prev => ({ ...prev, [name]: val }))
    setSaving(prev => ({ ...prev, [name]: true }))
    const settingId = settings?.[name + "_setting_id"]
    try {
      await api.post("update-setting/" + settingId, { setting: val })
      setLastSaved(name)
      setTimeout(() => setLastSaved(""), 2000)
    } catch (e) { console.error("Save failed:", e) }
    setSaving(prev => ({ ...prev, [name]: false }))
  }

  // Auto-save text input (debounced)
  const changeTextHandler = (name, val) => {
    setSettings(prev => ({ ...prev, [name]: val }))
    clearTimeout(window[`_debounce_${name}`])
    window[`_debounce_${name}`] = setTimeout(async () => {
      setSaving(prev => ({ ...prev, [name]: true }))
      const settingId = settings?.[name + "_setting_id"]
      try {
        await api.post("update-setting/" + settingId, { setting: val })
        setLastSaved(name)
        setTimeout(() => setLastSaved(""), 2000)
      } catch (e) { console.error(e) }
      setSaving(prev => ({ ...prev, [name]: false }))
    }, 800)
  }

  // Save dual-list params
  const saveDualList = async (name, jsonStr) => {
    setSettings(prev => ({ ...prev, [name]: jsonStr }))
    const settingId = settings?.[name + "_setting_id"]
    if (!settingId) return
    setSaving(prev => ({ ...prev, [name]: true }))
    try {
      await api.post("update-setting/" + settingId, { setting: jsonStr })
      setLastSaved(name)
      setTimeout(() => setLastSaved(""), 2000)
    } catch (e) { console.error(e) }
    setSaving(prev => ({ ...prev, [name]: false }))
  }

  const handleReset = async () => {
    setPageLoad(true)
    try {
      const resp = await api.post("reset-settings/" + ccId + "?center_type=cc")
      setSettings(resp.data)
    } catch (e) { console.error(e) }
    setPageLoad(false)
  }

  // Check dependency — uses current settings state for live conditional visibility
  const isDepMet = (dep) => {
    if (!dep) return true
    const currentVal = String(settings?.[dep.field] ?? "")
    return dep.val.includes(currentVal)
  }

  // Section Header
  const SectionHead = ({ sec }) => (
    <div style={{
      padding: "14px 18px", marginBottom: "16px", marginTop: "20px", borderRadius: "10px",
      background: `linear-gradient(135deg, ${sec.color}12, ${sec.color}08)`,
      borderLeft: `4px solid ${sec.color}`
    }}>
      <h5 style={{ margin: 0, color: sec.color, fontWeight: 700, fontSize: "16px" }}>
        <i className={`fas ${sec.icon}`} style={{ marginRight: "8px" }}></i>{sec.title}
      </h5>
    </div>
  )

  // Render a single field
  const renderField = (f) => {
    // Skip if dependency not met
    if (f.dep && !isDepMet(f.dep)) return null

    const isSaving = saving[f.n]
    const justSaved = lastSaved === f.n
    const cardStyle = {
      background: "#fff", borderRadius: "10px", padding: "16px", marginBottom: "12px",
      border: justSaved ? "2px solid #22c55e" : "1px solid #e2e8f0",
      boxShadow: "0 1px 3px rgba(0,0,0,0.06)", transition: "border-color 0.3s ease",
      position: "relative"
    }
    const statusIcon = () => (
      <>
        {isSaving && <div style={{ position: "absolute", top: 8, right: 8 }}><Spinner size="sm" color="primary" /></div>}
        {justSaved && <div style={{ position: "absolute", top: 8, right: 8, color: "#22c55e", fontSize: "12px", fontWeight: 600 }}>✓ Saved</div>}
      </>
    )
    const noteEl = f.note ? <small className="text-muted d-block mt-1" style={{ fontSize: "11px" }}><i className="fas fa-info-circle me-1"></i>{f.note}</small> : null

    if (f.t === "radio") {
      return (
        <Col md={6} key={f.n}>
          <div style={cardStyle}>
            {statusIcon()}
            <AvRadioGroup inline name={f.n} label={f.l} value={String(settings?.[f.n] ?? f.def ?? "")}
              onChange={e => changeHandler(f.n, e.target.value)}>
              {f.o.map((opt, i) => <AvRadio key={i} label={opt.l} value={String(opt.v)} />)}
            </AvRadioGroup>
            {noteEl}
          </div>
        </Col>
      )
    }

    if (f.t === "select") {
      return (
        <Col md={6} key={f.n}>
          <div style={cardStyle}>
            {statusIcon()}
            <label style={{ fontWeight: 600, fontSize: "13px", color: "#334155", marginBottom: "8px", display: "block" }}>{f.l}</label>
            <select className="form-control" value={settings?.[f.n] || ""} onChange={e => changeHandler(f.n, e.target.value)}>
              <option value="">Select</option>
              {f.o.map((opt, i) => <option key={i} value={opt.v}>{opt.l}</option>)}
            </select>
            {noteEl}
          </div>
        </Col>
      )
    }

    // text input
    return (
      <Col md={6} key={f.n}>
        <div style={cardStyle}>
          {statusIcon()}
          <label style={{ fontWeight: 600, fontSize: "13px", color: "#334155", marginBottom: "8px", display: "block" }}>{f.l}</label>
          <input type="text" className="form-control" value={settings?.[f.n] ?? f.def ?? ""}
            onChange={e => changeTextHandler(f.n, e.target.value)} />
          {noteEl}
        </div>
      </Col>
    )
  }

  // Dual list for parameters (export, print)
  const DualList = ({ name, label, allItems, depField }) => {
    if (depField && !isDepMet(depField)) return null
    let selected = []
    try { selected = JSON.parse(settings?.[name] || "[]") } catch { selected = [] }
    const available = allItems.filter(i => !selected.includes(i))
    const add = (item) => saveDualList(name, JSON.stringify([...selected, item]))
    const remove = (item) => saveDualList(name, JSON.stringify(selected.filter(i => i !== item)))
    const moveUp = (idx) => {
      if (idx === 0) return
      const items = [...selected]; [items[idx - 1], items[idx]] = [items[idx], items[idx - 1]]
      saveDualList(name, JSON.stringify(items))
    }
    const moveDown = (idx) => {
      if (idx >= selected.length - 1) return
      const items = [...selected]; [items[idx], items[idx + 1]] = [items[idx + 1], items[idx]]
      saveDualList(name, JSON.stringify(items))
    }
    const justSaved = lastSaved === name
    const iStyle = { padding: "8px 12px", marginBottom: "4px", background: "#6b7280", color: "#fff", borderRadius: "4px", cursor: "pointer", fontSize: "13px", display: "flex", justifyContent: "space-between", alignItems: "center" }
    return (
      <div style={{
        background: "#fff", borderRadius: "10px", padding: "16px", marginBottom: "16px",
        border: justSaved ? "2px solid #22c55e" : "1px solid #e2e8f0", transition: "border-color 0.3s ease"
      }}>
        <label style={{ fontWeight: 600, fontSize: "14px", color: "#334155", marginBottom: "12px", display: "block" }}>
          <i className="fas fa-grip-vertical" style={{ marginRight: "6px", opacity: 0.6 }}></i>{label}
          {justSaved && <span style={{ color: "#22c55e", fontSize: "11px", marginLeft: "8px" }}>✓ Saved</span>}
        </label>
        <Row>
          <Col md={6}>
            <div style={{ fontSize: "12px", fontWeight: 600, color: "#64748b", marginBottom: "6px" }}>Available</div>
            <div style={{ maxHeight: "300px", overflowY: "auto" }}>
              {available.map((item, i) => (
                <div key={i} style={iStyle} onClick={() => add(item)}>{item} <i className="fas fa-plus" style={{ fontSize: "10px" }}></i></div>
              ))}
            </div>
          </Col>
          <Col md={6}>
            <div style={{ fontSize: "12px", fontWeight: 600, color: "#64748b", marginBottom: "6px" }}>Selected (Click arrows to reorder)</div>
            <div style={{ maxHeight: "300px", overflowY: "auto", minHeight: "40px" }}>
              {selected.map((item, index) => (
                <div key={item} style={{ ...iStyle, background: "#4f46e5" }}>
                  <span>{item}</span>
                  <span style={{ display: "flex", gap: "6px", alignItems: "center" }}>
                    <i className="fas fa-arrow-up" style={{ fontSize: "10px", cursor: "pointer", opacity: index === 0 ? 0.3 : 1 }} onClick={ev => { ev.stopPropagation(); moveUp(index) }}></i>
                    <i className="fas fa-arrow-down" style={{ fontSize: "10px", cursor: "pointer", opacity: index >= selected.length - 1 ? 0.3 : 1 }} onClick={ev => { ev.stopPropagation(); moveDown(index) }}></i>
                    <i className="fas fa-times" style={{ fontSize: "10px", cursor: "pointer" }} onClick={ev => { ev.stopPropagation(); remove(item) }}></i>
                  </span>
                </div>
              ))}
            </div>
          </Col>
        </Row>
      </div>
    )
  }

  if (pageLoad) return (
    <div className="dashBody"><div className="text-center py-5">
      <Spinner color="primary" style={{ width: "3rem", height: "3rem" }} />
      <p className="mt-3 text-muted">Loading CC Settings...</p>
    </div></div>
  )

  return (
    <div className="dashBody">
      <div className="font-size-18 mb-0 dashHeader">
        <Row>
          <Col md={6}>
            <h4>
              <i className="fas fa-warehouse" style={{ marginRight: "8px", color: "#0ea5e9" }}></i>
              CC Settings <small className="text-muted" style={{ fontSize: "13px" }}>[<span className="text-danger">* required</span>]</small>
              {settings?.cc_name && <span style={{ fontSize: "14px", color: "#6c757d", marginLeft: "10px" }}>— {settings.cc_name} {settings.cc_number ? `(${settings.cc_number})` : ""}</span>}
            </h4>
          </Col>
          <Col md={6} style={{ textAlign: "right" }}>
            <button className="btn btn-outline-warning btn-sm me-2" onClick={() => setResetConfirm(true)} title="Reset to company defaults">
              <i className="fas fa-undo"></i> Reset to Company Defaults
            </button>
            <Link className="dashBtn" to="/chilling-centers">
              <i className="fas fa-chevron-left"></i> <b>Back</b>
            </Link>
          </Col>
        </Row>
      </div>

      <div style={{ padding: "0 8px" }}>
        <AvForm className="form-horizontal">

          {/* Render ALL CC sections from ccSettingsConfig */}
          {CC_SECTIONS.map((sec, si) => (
            <div key={si}>
              <SectionHead sec={sec} />
              <Row>{sec.fields.map(f => renderField(f))}</Row>
            </div>
          ))}

          {/* 81. Export Parameters (only when #77 = Yes) */}
          {isDepMet({ field: "auto_export_session_close", val: ["1"] }) && (
            <>
              <SectionHead sec={{ title: "81. Export Parameters", icon: "fa-file-export", color: "#14b8a6" }} />
              <DualList name="export_parameters" label="Export Parameters" allItems={CC_EXPORT_PARAMS} />
            </>
          )}

          {/* 96. Print Parameters */}
          <SectionHead sec={{ title: "96. Print Parameters", icon: "fa-print", color: "#d97706" }} />
          <DualList name="print_parameters" label="Print Parameters" allItems={CC_PRINT_PARAMS} />

        </AvForm>
      </div>

      {resetConfirm && (
        <SweetAlert title="Reset All Settings?" warning showCancel confirmBtnBsStyle="danger" cancelBtnBsStyle="secondary"
          onConfirm={() => { handleReset(); setResetConfirm(false) }}
          onCancel={() => setResetConfirm(false)}>
          This will reset ALL CC settings to company defaults. This cannot be undone!
        </SweetAlert>
      )}
    </div>
  )
}

export default CcMobileSettings
